import { pageTitle } from '@palaccount/config';
import type { TimesheetEntryResponse } from '@palaccount/contracts';
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Hours, Ltr } from '@/components/amount';
import { ConfirmBox } from '@/components/confirm-box';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { Typeahead } from '@/components/ui/typeahead';
import { TableCell } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Modal } from '@/features/inventory/modal';
import { useCan } from '@/lib/permissions';
import { localizeServerCause, localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';
import { useCompanyToday } from '@/lib/session';

import {
  useApprovals,
  useApproveWeek,
  useBoard,
  useRejectWeek,
  useSubmitWeek,
  useUpsertCell,
  useWeek,
} from './api';
import { useProjectPicker } from './project-picker';
import { ProjectsTabs } from './projects-tabs';
import { TimerCard } from './timer-card';

/**
 * PRJ-03 — Timesheets: the weekly grid (my week by default), submit, and
 * the approvals queue for holders of projects.approve.
 *
 * The lock (BR-PRJ-03-05): a submitted or approved week greys every cell,
 * hides Submit, and shows the padlock note — while the server and the
 * database refuse regardless. Hiding Submit is also ERR-PRJ-03-02, whose §14
 * row prescribes doing nothing about it: "Looked for Submit after submitting |
 * It has disappeared | Nothing." Zero deletes an entry (BR-PRJ-03-02 — a
 * different rule from the ERR of the same number; PRJ-03's two tables number
 * independently, which is worth knowing before reading either as the other).
 *
 * The billable flag is VISIBLE AND EDITABLE on the add-row form, pre-filled
 * from the project (BR-PRJ-03-03, the ruled fix — no entry point stores a
 * value the user could not see; EC-19-64's quick-entry-forced-billable
 * branch is gone with the mode, and N-15 / GEN-17a — no presentation choice
 * may change what is stored — is the rule that catches the next one, with
 * GEN-17's other half beside it: a system-supplied default is SHOWN, never
 * silent). Approving shows the exact confirmation
 * first (BR-PRJ-03-07); rejecting requires a comment and the button stays
 * disabled without one (BR-PRJ-03-08 + the silent-nothing defect, fixed).
 */

function sundayOf(date: Date): string {
  const copy = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  copy.setUTCDate(copy.getUTCDate() - copy.getUTCDay());
  return copy.toISOString().slice(0, 10);
}

function addDaysIso(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/**
 * G-09: the add-row form's date is the company's today while the week on
 * screen contains it — "anywhere there is a date, the natural thing is
 * today". Paging to another week puts the date on that week's first day,
 * because today is not a day a row on that week can carry.
 */
export function defaultWorkDate(weekStart: string, today: string): string {
  return today >= weekStart && today <= addDaysIso(weekStart, 6) ? today : weekStart;
}

interface GridRow {
  projectId: string;
  projectName: string;
  taskId: string;
  taskTitle: string;
  billable: boolean;
  entries: Map<string, TimesheetEntryResponse>;
  total: number;
}

export function TimesheetsPage() {
  const { t } = useTranslation();
  const canApprove = useCan('projects.approve');
  const today = useCompanyToday();
  const [weekStart, setWeekStart] = useState(() => sundayOf(new Date(`${today}T00:00:00Z`)));
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    document.title = pageTitle(t('prj.timesheetsTitle'));
  }, [t]);

  const { data: week } = useWeek(weekStart);
  const upsert = useUpsertCell();
  const submit = useSubmitWeek();

  const act = (promise: Promise<unknown>) => {
    setError('');
    promise.catch((cause: unknown) => {
      // The cause reaches the decision — a refusal keeps the server's sentence
      // localized, a request that never answered gets this screen's own. The
      // OTHER `localizeServerMessage` call in this file is not a cause at all:
      // it localizes a sentence carried in a 2xx result body, so it stays.
      setError(localizeServerCause(t, cause, 'prj.errSaveFailedTimesheet'));
    });
  };

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDaysIso(weekStart, index)),
    [weekStart],
  );
  const dayNames = [
    t('prj.daySun'),
    t('prj.dayMon'),
    t('prj.dayTue'),
    t('prj.dayWed'),
    t('prj.dayThu'),
    t('prj.dayFri'),
    t('prj.daySat'),
  ];

  // Each grid row is a project-and-task combination (§4). Adjusting entries
  // are corrections about a past period, not grid cells — listed separately.
  const rows = useMemo(() => {
    const map = new Map<string, GridRow>();
    for (const entry of week?.entries ?? []) {
      if (entry.adjusting) continue;
      const key = `${entry.projectId}:${entry.taskId}`;
      const row = map.get(key) ?? {
        projectId: entry.projectId,
        projectName: entry.projectName,
        taskId: entry.taskId,
        taskTitle: entry.taskTitle,
        billable: entry.billable,
        entries: new Map<string, TimesheetEntryResponse>(),
        total: 0,
      };
      row.entries.set(entry.workDate, entry);
      row.billable = entry.billable;
      row.total += Number.parseFloat(entry.hours);
      map.set(key, row);
    }
    return [...map.values()];
  }, [week]);

  const locked = week?.locked ?? false;
  const hasDrafts = (week?.entries ?? []).some((entry) => entry.status === 'draft');

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {t('prj.eyebrow')}
        </p>
        <h1 className="text-2xl font-semibold">{t('prj.timesheetsTitle')}</h1>
      </div>
      <ProjectsTabs />

      {error !== '' ? (
        <p role="alert" className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {error}
        </p>
      ) : null}
      {notice !== '' ? (
        <p role="status" className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300">
          {notice}
        </p>
      ) : null}

      {/* §5e: workdays with no hours at all — never future, never weekend. */}
      {week && week.missingDays.length > 0 && !locked ? (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          <span className="font-semibold">{t('prj.missingTitle')}</span>{' '}
          {week.missingDays.map((day) => <Ltr key={day} value={day} className="me-2" />)}
          <span className="text-xs">{t('prj.missingSub')}</span>
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          aria-label={t('prj.prevWeek')}
          onClick={() => setWeekStart(addDaysIso(weekStart, -7))}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">
          <Ltr value={`${weekStart} – ${addDaysIso(weekStart, 6)}`} />
        </span>
        <Button
          variant="outline"
          size="sm"
          aria-label={t('prj.nextWeek')}
          onClick={() => setWeekStart(addDaysIso(weekStart, 7))}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        {week ? (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
              week.status === 'draft' && 'bg-muted text-muted-foreground',
              week.status === 'submitted' && 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
              week.status === 'approved' && 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
              week.status === 'rejected' && 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
            )}
          >
            {t(`prj.ws${week.status}`)}
          </span>
        ) : null}
        <span className="ms-auto text-sm text-muted-foreground">
          {t('prj.weekTotal')} <Hours value={week?.totalHours ?? '0.00'} />
        </span>
      </div>

      {week && week.status === 'rejected' && week.rejectionComment !== null ? (
        <p className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
          {t('prj.rejectedComment')} {week.rejectionComment}
        </p>
      ) : null}

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="p-3 text-start">{t('prj.colProjectTask')}</th>
                {days.map((day, index) => (
                  <th key={day} className="p-2 text-center">
                    {dayNames[index]}
                    <div className="font-normal">
                      <Ltr value={day.slice(8)} />
                    </div>
                  </th>
                ))}
                <th className="p-2 text-end">{t('prj.colTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <TableCell colSpan={9} className="px-6 text-center text-muted-foreground">
                    {t('prj.emptyWeek')}
                  </TableCell>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={`${row.projectId}:${row.taskId}`} className="border-b last:border-0">
                    <TableCell className="px-3">
                      <div className="font-medium">{row.taskTitle}</div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {row.projectName}
                        {row.billable ? (
                          <span className="rounded-full bg-emerald-100 px-1.5 text-[10px] font-semibold text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                            {t('prj.billPill')}
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    {days.map((day) => (
                      <Cell
                        key={day}
                        row={row}
                        day={day}
                        locked={locked}
                        onSave={(hours) =>
                          act(
                            upsert.mutateAsync({
                              projectId: row.projectId,
                              taskId: row.taskId,
                              workDate: day,
                              hours,
                              billable: row.billable,
                            }),
                          )
                        }
                      />
                    ))}
                    <TableCell className="px-2 text-end font-medium tabular-nums">
                      <Ltr value={row.total.toFixed(2)} />
                    </TableCell>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {!locked ? <AddRowForm weekStart={weekStart} act={act} /> : null}

      <div className="flex items-center justify-between">
        {locked ? (
          <p className="flex items-center gap-1 text-sm text-muted-foreground">
            <Lock className="h-4 w-4" /> {t('prj.lockedNote')}
          </p>
        ) : (
          <span />
        )}
        {!locked && hasDrafts ? (
          <Button
            onClick={() =>
              act(
                submit.mutateAsync(weekStart).then(() => {
                  setNotice('');
                }),
              )
            }
          >
            {t('prj.submitWeek')}
          </Button>
        ) : null}
      </div>

      {/* §5f / §6: the Timer is "always available" — it logs to TODAY, so it
          stays even while the DISPLAYED week is locked; a locked target week
          is the server's refusal to make, not this card's. */}
      <TimerCard act={act} />

      {/* The approvals queue is a READ of other people's weeks, and the
          server refuses it without projects.approve (BR-PRJ-03-15). A panel of
          rows nobody may fetch is not what 16.7 asks for — 16.7 row 1's
          "absent" governs a whole panel, row 2 governs the buttons inside
          one — so this one stays conditional and is corrected in
          PERMISSION-UI.md rather than in the code.

          ERR-PRJ-03-06 — "Switched to Summary and lost the approvals queue" —
          is the prototype hiding this panel inside its Weekly grid tab. The
          defects list says "show it on every tab, or give it its own"; this
          screen's answer is structural: ONE page, no intra-page tabs, and the
          queue rendered here at page level beside the grid and the Timer card
          alike — there is no surface a manager can switch to that loses it. */}
      {canApprove ? <ApprovalsQueue onNotice={setNotice} act={act} /> : null}
    </div>
  );
}

function Cell({
  row,
  day,
  locked,
  onSave,
}: {
  row: GridRow;
  day: string;
  locked: boolean;
  onSave: (hours: string) => void;
}) {
  const { t } = useTranslation();
  const entry = row.entries.get(day);
  const [value, setValue] = useState(entry?.hours ?? '');

  useEffect(() => {
    setValue(entry?.hours ?? '');
  }, [entry?.hours]);

  return (
    <TableCell className="px-1 text-center">
      <Input
        aria-label={t('prj.cellLabel', { task: row.taskTitle, day })}
        value={value}
        disabled={locked}
        inputMode="decimal"
        className={cn('h-8 w-14 text-center', locked && 'bg-muted')}
        onChange={(event) => setValue(event.target.value)}
        onBlur={() => {
          const trimmed = value.trim();
          const before = entry?.hours ?? '';
          if (trimmed === before || (trimmed === '' && before === '')) return;
          // BR-PRJ-03-02: an emptied or zeroed cell deletes the entry.
          onSave(trimmed === '' ? '0' : trimmed);
        }}
      />
    </TableCell>
  );
}

/**
 * The add-row form — the entry form of BR-PRJ-03-03: project, task, hours,
 * date, and the billable flag VISIBLE, PRE-FILLED from the project and
 * EDITABLE before anything is saved.
 */
function AddRowForm({ weekStart, act }: { weekStart: string; act: (p: Promise<unknown>) => void }) {
  const { t } = useTranslation();
  const upsert = useUpsertCell();
  const [projectId, setProjectId] = useState('');
  const [taskId, setTaskId] = useState('');
  const today = useCompanyToday();
  const [workDate, setWorkDate] = useState(defaultWorkDate(weekStart, today));
  const [hours, setHours] = useState('');
  const [billable, setBillable] = useState(true);
  const [billableTouched, setBillableTouched] = useState(false);
  const { data: board } = useBoard(projectId === '' ? undefined : projectId);
  // GEN-50 — the entry row's project field, searched rather than holding the
  // portfolio. `byId` carries the whole record, which is what BR-PRJ-03-03's
  // pre-fill below needs: it reads the chosen project's `billable` flag.
  const projectPicker = useProjectPicker(projectId === '' ? null : projectId);
  const project = projectPicker.byId.get(projectId);

  useEffect(() => {
    setWorkDate(defaultWorkDate(weekStart, today));
  }, [weekStart, today]);

  // BR-PRJ-03-03: pre-fill from the project — until the user chooses.
  useEffect(() => {
    if (!billableTouched && project) setBillable(project.billable);
  }, [project, billableTouched]);

  return (
    <Card>
      <CardContent className="flex flex-wrap items-end gap-2 p-4">
        <Typeahead
          label={t('prj.fProject')}
          placeholder={t('prj.fProject')}
          value={projectId === '' ? null : projectId}
          onChange={(id) => {
            setProjectId(id ?? '');
            setTaskId('');
            setBillableTouched(false);
          }}
          options={projectPicker.options}
          selected={projectPicker.optionFor(projectId)}
          query={projectPicker.query}
          onQueryChange={projectPicker.setQuery}
          emptyLabel={t('picker.noMatchingProject')}
          loading={projectPicker.loading}
          className="w-52"
        />
        <NativeSelect
          aria-label={t('prj.colTask')}
          value={taskId}
          onChange={(event) => setTaskId(event.target.value)}
          className="w-52"
        >
          <option value="">{t('prj.colTask')}</option>
          {(board?.tasks ?? []).map((task) => (
            <option key={task.taskId} value={task.taskId}>
              {task.title}
            </option>
          ))}
        </NativeSelect>
        <Input
          aria-label={t('common.date')}
          type="date"
          value={workDate}
          onChange={(event) => setWorkDate(event.target.value)}
          className="w-36"
        />
        <Input
          aria-label={t('prj.fHours')}
          placeholder={t('prj.fHours')}
          inputMode="decimal"
          value={hours}
          onChange={(event) => setHours(event.target.value)}
          className="w-20"
        />
        {/* Always visible, always editable (BR-PRJ-03-03). */}
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={billable}
            onChange={(event) => {
              setBillable(event.target.checked);
              setBillableTouched(true);
            }}
          />
          {t('prj.fBillable')}
        </label>
        <Button
          variant="outline"
          disabled={projectId === '' || taskId === '' || hours === '' || Number.parseFloat(hours) <= 0}
          onClick={() => {
            act(
              upsert.mutateAsync({ projectId, taskId, workDate, hours, billable }),
            );
            setHours('');
          }}
        >
          {t('prj.addEntry')}
        </Button>
      </CardContent>
    </Card>
  );
}

function ApprovalsQueue({
  onNotice,
  act,
}: {
  onNotice: (message: string) => void;
  act: (promise: Promise<unknown>) => void;
}) {
  const { t } = useTranslation();
  const { data: approvals } = useApprovals(true);
  const approve = useApproveWeek();
  const reject = useRejectWeek();
  const [confirming, setConfirming] = useState<string>();
  const [rejecting, setRejecting] = useState<{ weekId: string; userName: string | null }>();
  const [comment, setComment] = useState('');

  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <h2 className="text-sm font-semibold">{t('prj.approvalsTitle')}</h2>
        {(approvals ?? []).length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('prj.noApprovals')}</p>
        ) : (
          <ul className="space-y-2">
            {(approvals ?? []).map((row) => (
              <li key={row.weekId} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span>
                  <span className="font-medium">{row.userName ?? '—'}</span>{' '}
                  <span className="text-muted-foreground">
                    {t('prj.weekOf')} <Ltr value={row.weekStart} /> ·{' '}
                    <Hours value={row.totalHours} /> ·{' '}
                    {t('prj.billableSplit', {
                      billable: row.billableHours,
                      non: row.nonBillableHours,
                    })}
                  </span>
                </span>
                <span className="flex gap-2">
                  <Button size="sm" onClick={() => setConfirming(row.weekId)}>
                    {t('prj.approve')}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setComment('');
                      setRejecting({ weekId: row.weekId, userName: row.userName });
                    }}
                  >
                    {t('prj.reject')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}

        {/*
          16.5.3 names approving a timesheet week as one of the THREE places
          the product's one shared confirmation box is used, and ADM-02 §8
          says why it matters: "a build must reuse it rather than invent
          another." This raised its own bordered panel — no padlock, no amber
          tile, not 420px, backdrop dismissal by a different mechanism — which
          is a fourth pattern BR-PERM-15 forbids. The body is still
          BR-PRJ-03-07's exact sentence and the button still carries the verb.
        */}
        <ConfirmBox
          open={confirming !== undefined}
          title={t('prj.approveTitle')}
          body={t('prj.approveConfirm')}
          confirmLabel={t('prj.approveDo')}
          busy={approve.isPending}
          onCancel={() => setConfirming(undefined)}
          onConfirm={() => {
            const weekId = confirming;
            if (weekId === undefined) return;
            setConfirming(undefined);
            act(
              approve.mutateAsync(weekId).then((result) => {
                onNotice(
                  localizeServerMessage(t, (result as { message?: string }).message ?? ''),
                );
              }),
            );
          }}
        />

        {rejecting !== undefined ? (
          <Modal title={t('prj.rejectTitle')} onClose={() => setRejecting(undefined)}>
            <p className="text-sm text-muted-foreground">
              {rejecting.userName ?? '—'} — {t('prj.rejectSub')}
            </p>
            <Textarea
              aria-label={t('prj.rejectComment')}
              placeholder={t('prj.rejectPh')}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              rows={3}
              className="mt-2"
            />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRejecting(undefined)}>
                {t('common.cancel')}
              </Button>
              {/* The confirm is VISIBLY disabled on an empty comment. */}
              <Button
                variant="destructive"
                disabled={comment.trim() === ''}
                onClick={() => {
                  const target = rejecting;
                  setRejecting(undefined);
                  act(reject.mutateAsync({ weekId: target.weekId, comment: comment.trim() }));
                }}
              >
                {t('prj.rejectDo')}
              </Button>
            </div>
          </Modal>
        ) : null}
      </CardContent>
    </Card>
  );
}
